import React, { useMemo, useState } from "react";

import ace from "brace";
import "brace/mode/json";
import "brace/theme/github";
import { JsonEditor } from "jsoneditor-react";
import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { type LabelRule } from "@north-beam/nb-common";

import {
  type AdvancedSegmentDescriptor,
  type AdvancedSegmentLevel,
} from "./types";

const EditorContentSchema = z
  .object({
    name: z.string(),
    output: z.string(),
    active: z.boolean(),
    priority: z.number(),
    matcher: z.any(),
    createdAt: z.string(),
  })
  .array();

function valueToSegment(
  value: any,
  level: AdvancedSegmentLevel,
): AdvancedSegmentDescriptor {
  return {
    id: uuidv4(),
    level,
    name: value.name,
    output: value.output,
    active: value.active,
    priority: value.priority,
    matcher: cloneDeep(value.matcher),
    createdAt: value.createdAt,
    errors: {
      name: null,
      output: null,
      priority: null,
      matcher: null,
      level: null,
    },
  };
}

interface Props {
  segments: AdvancedSegmentDescriptor[];
  onChange: (segments: AdvancedSegmentDescriptor[]) => void;
}

export const RawSegmentsEditor = ({ segments, onChange }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [campaignError, setCampaignError] = useState(false);
  const [adsetError, setAdsetError] = useState(false);
  const [adError, setAdError] = useState(false);

  const tree = useMemo(() => {
    const result: Record<AdvancedSegmentLevel, LabelRule[]> = {
      campaign: [],
      adset: [],
      ad: [],
    };

    segments.forEach((segment) => {
      result[segment.level].push({
        name: segment.name,
        output: segment.output,
        active: segment.active,
        priority: segment.priority,
        matcher: cloneDeep(segment.matcher),
        createdAt: segment.createdAt || new Date().toISOString(),
      });
    });

    return result;
  }, [segments]);

  return (
    <div>
      <div className="mb-8">
        <div className="text-sm font-bold uppercase mb-2 flex items-center gap-3">
          Campaign level
          {campaignError && (
            <div className="text-xxs text-mars mt-1">*Invalid content</div>
          )}
        </div>
        <JsonEditor
          value={tree.campaign}
          onChange={(content: any) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo:
                "Change campaign label rules using advanced editor",
            });

            const result = EditorContentSchema.safeParse(content);

            if (result.success) {
              setCampaignError(false);

              const segments: AdvancedSegmentDescriptor[] = [];

              content.forEach((value: any) => {
                segments.push(valueToSegment(value, "campaign"));
              });

              tree.adset.forEach((value: any) => {
                segments.push(valueToSegment(value, "adset"));
              });

              tree.ad.forEach((value: any) => {
                segments.push(valueToSegment(value, "ad"));
              });

              onChange(segments);
            } else {
              setCampaignError(true);
            }
          }}
          mode="code"
          ace={ace}
          htmlElementProps={{ style: { height: 500 } }}
        />
      </div>
      <div className="mb-8">
        <div className="text-sm font-bold uppercase mb-2 flex items-center gap-3">
          Adset level
          {adsetError && (
            <div className="text-xxs text-mars mt-1">*Invalid content</div>
          )}
        </div>
        <JsonEditor
          value={tree.adset}
          onChange={(content: any) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo:
                "Change adset label rules using advanced editor",
            });

            const result = EditorContentSchema.safeParse(content);

            if (result.success) {
              setAdsetError(false);

              const segments: AdvancedSegmentDescriptor[] = [];

              tree.campaign.forEach((value: any) => {
                segments.push(valueToSegment(value, "campaign"));
              });

              content.forEach((value: any) => {
                segments.push(valueToSegment(value, "adset"));
              });

              tree.ad.forEach((value: any) => {
                segments.push(valueToSegment(value, "ad"));
              });

              onChange(segments);
            } else {
              setAdsetError(true);
            }
          }}
          mode="code"
          ace={ace}
          htmlElementProps={{ style: { height: 500 } }}
        />
      </div>
      <div>
        <div className="text-sm font-bold uppercase mb-2 flex items-center gap-3">
          Ad level
          {adError && (
            <div className="text-xxs text-mars mt-1">*Invalid content</div>
          )}
        </div>
        <JsonEditor
          value={tree.ad}
          onChange={(content: any) => {
            prettyLogEventPage({
              domain: "Breakdown v2",
              action: "onChange",
              additionalActionInfo:
                "Change ad label rules using advanced editor",
            });

            const result = EditorContentSchema.safeParse(content);

            if (result.success) {
              setAdError(false);

              const segments: AdvancedSegmentDescriptor[] = [];

              tree.campaign.forEach((value: any) => {
                segments.push(valueToSegment(value, "campaign"));
              });

              tree.adset.forEach((value: any) => {
                segments.push(valueToSegment(value, "adset"));
              });

              content.forEach((value: any) => {
                segments.push(valueToSegment(value, "ad"));
              });

              onChange(segments);
            } else {
              setAdError(true);
            }
          }}
          mode="code"
          ace={ace}
          htmlElementProps={{ style: { height: 500 } }}
        />
      </div>
    </div>
  );
};
